import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import { useServiceFeedbackPaginated } from "../../../../../GoService/api/queries/useServiceFeedback";
import {
  ServiceFeedbackCard,
  ServiceFeedbackCardLoader,
} from "../../../../../GoService/components/ServiceFeedbackCard";

import EmptyReviewsFigure from "../../../../assets/images/empty-reviews.png";

import Slider from "../Slider";
import { useEffect } from "react";

const REVIEW_LIMIT = 5;

const ServicesReviewsList = ({
  fullName,
  entityUserId,
  isOwn,
  isDataProfileDataLoading,
  handleSetHasReviews,
}: {
  fullName: string;
  entityUserId?: string;
  isOwn?: boolean;
  isDataProfileDataLoading?: boolean;
  handleSetHasReviews: (hasReviews: boolean) => void;
}) => {
  const { userId } = useParams();

  const ID = entityUserId || userId;

  const {
    data: reviews,
    isLoading: isUserReviewsLoading,
    isError: iseUserReviewsHasError,
  } = useServiceFeedbackPaginated({
    userId: Number(ID),
    limit: REVIEW_LIMIT,
  });

  useEffect(() => {
    if (isUserReviewsLoading) return;
    if (!reviews || reviews.length === 0) return;
    handleSetHasReviews(true);
  }, [reviews, isUserReviewsLoading, handleSetHasReviews]);

  const IS_LOADING = isUserReviewsLoading || Boolean(isDataProfileDataLoading);

  if (!reviews && !isUserReviewsLoading) return null;

  if (iseUserReviewsHasError || (!isUserReviewsLoading && !reviews) || (!isUserReviewsLoading && reviews?.length === 0))
    return <EmptyList fullName={fullName} isOwn={isOwn} />;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.container}>
        <Slider
          items={reviews?.map((feedback) => <ServiceFeedbackCard key={feedback.id} feedback={feedback} />) || []}
          isLoading={IS_LOADING}
          loadingComponent={<ServiceFeedbackCardLoader />}
          loadingCount={REVIEW_LIMIT}
        />
      </Box>
    </Box>
  );
};

const EmptyList = ({ fullName, isOwn }: { fullName: string; isOwn?: boolean }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "goService" });
  return (
    <Box sx={styles.emptyContainer}>
      <Box sx={styles.emptyImage} component="img" src={EmptyReviewsFigure} alt="Empty Reviews" />
      <Typography sx={styles.emptyText}>
        {isOwn ? t("noReviews_own") : t("noReviews", { userName: fullName })}
      </Typography>
    </Box>
  );
};

const styles = {
  root: {
    width: "100%",
  },
  container: {
    position: "relative" as const,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
  },
  emptyImage: {
    width: "107px",
    height: "80px",
  },
  emptyText: {
    color: "var(--Dark---800, #414141)",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};

export default ServicesReviewsList;
